import { STATUSES } from 'constants/common';
import _orderBy from 'lodash/orderBy';
import parse from 'date-fns/parse';
import _uniqBy from 'lodash/uniqBy';
import _flatten from 'lodash/flatten';

import { formatDate } from 'helpers/formatDate';
import { getTranslation } from 'helpers/getTranslation';

type CourseOption = { id: string; title?: string | null };

export const getSelectCourseOptions = (courses: CourseOption[]) =>
  courses.map((course: any) => ({
    label: course.title || getTranslation('common.text.untitled'),
    value: course.id,
  }));

export const getCurrentCourseOption = (
  course: CourseOption | null | undefined,
) => {
  if (!course) {
    return null;
  }

  return {
    label: course.title || getTranslation('common.text.untitled'),
    value: course.id,
  };
};

type ProgressSummary = {
  completed: number;
  inProgress: number;
  notStarted: number;
};

export const prepareOverallProgress = (data: ProgressSummary) => {
  const progressData = [
    {
      id: 'completed',
      label: getTranslation('dashboard_page.helpers.completed'),
      value: data.completed,
      color: 'rgb(133, 224, 171)',
    },
    {
      id: 'inProgress',
      label: getTranslation('dashboard_page.helpers.in_progress'),
      value: data.inProgress,
      color: 'rgb(112, 180, 250)',
    },
    {
      id: 'notStarted',
      label: getTranslation('dashboard_page.helpers.not_started'),
      value: data.notStarted,
      color: 'rgb(253, 206, 130)',
    },
  ];

  if (data.completed === 0 && data.notStarted === 0 && data.inProgress === 0) {
    return [
      { id: null as any, label: '', color: 'rgb(235, 250, 241)', value: true },
    ];
  }

  return progressData;
};

type StudentRow = {
  createdAt: string | number | Date;
  [key: string]: unknown;
};

type ChartPoint = { x: string; y: number };

export const prepareStudents = (students: StudentRow[]) => {
  const data = students.map((student: any) => ({
    ...student,
    date: formatDate(student.createdAt, 'yyyy-MM-dd'),
  }));

  const groupedData = _orderBy(
    data,
    [(student: any) => parse(student.date as string, 'yyyy-MM-dd', new Date())],
    ['asc'],
  ).reduce((acc: Record<string, ChartPoint>, item: { date?: string }) => {
    const dateKey = item.date ?? '';
    if (!acc[dateKey]) {
      acc[dateKey] = { x: dateKey, y: 1 };
    } else {
      acc[dateKey].y += 1;
    }

    return acc;
  }, {});

  return [
    {
      id: 'students',
      color: 'rgb(133, 224, 171)',
      data: Object.values(groupedData),
    },
  ];
};

type LessonProgress = {
  completedPercent: number;
  notStartedPercent: number;
};

export const prepareLessonsProgress = (lessons: LessonProgress[]) => {
  const lessonsCount = lessons.length;

  const completed = Number(
    (
      lessons.reduce(
        (lessonsCompleted: any, lesson: any) =>
          (lessonsCompleted += lesson.completedPercent),
        0,
      ) / lessonsCount
    ).toFixed(0),
  );
  const notStarted = Number(
    (
      lessons.reduce(
        (lessonsNotStarted: any, lesson: any) =>
          (lessonsNotStarted += lesson.notStartedPercent),
        0,
      ) / lessonsCount
    ).toFixed(0),
  );

  const completedData = [
    {
      id: 'completed',
      label: getTranslation('dashboard_page.helpers.completed'),
      value: completed,
      color: 'rgb(133, 224, 171)',
    },
    {
      id: 'notStarted',
      label: getTranslation('dashboard_page.helpers.not_started'),
      value: notStarted,
      color: 'rgb(223, 233, 249)',
    },
  ];

  const notStartedData = [
    {
      id: 'notStarted',
      label: getTranslation('dashboard_page.helpers.not_started'),
      value: notStarted,
      color: 'rgb(253, 206, 130)',
    },
    {
      id: 'empty',
      label: getTranslation('dashboard_page.helpers.empty'),
      value: completed,
      color: 'rgb(223, 233, 249)',
    },
  ];

  return {
    lessonsCount,
    completed,
    notStarted,
    completedData,
    notStartedData,
  };
};

type CourseWithStatus = {
  status?: string;
};

export const checkCourseStatsEmpty = ({
  course,
  courseStats,
}: {
  course: CourseWithStatus | null | undefined;
  courseStats: any;
}): boolean => {
  if (!courseStats || !course) {
    return true;
  }

  if (course.status !== STATUSES.PUBLISHED) {
    return true;
  }

  return false;
};

type School = { id: string; name?: string; language?: string };

type StatRow = {
  schools: School[];
};

export const prepareStudentsStats = (stats: StatRow[]) => {
  const schools = _uniqBy(
    _flatten(stats.map((stat: any) => stat.schools)),
    'id',
  );

  const colors = [
    'rgb(133, 224, 171)',
    'rgb(112, 180, 250)',
    'rgb(253, 206, 130)',
  ];

  const statsData = schools.map((school: any, schoolIndex: any) => {
    let label = '';

    if (school.name === 'Befront' && school.language === 'ru') {
      label = 'Befront RU';
    } else if (school.name === 'Befront' && school.language === 'en') {
      label = 'Befront EN';
    } else {
      label = school.name ?? '';
    }

    return {
      id: school.id,
      label,
      value: stats.filter((stat: any) => stat.schools[0].id === school.id)
        .length,
      color: colors[schoolIndex],
    };
  });

  return statsData;
};
